import { faSoapNote } from "@awesome.me/kit-0572d5a008/icons/kit/custom";
import {
  DSBadge,
  DSButton,
  DSCardWrapper,
  DSDropdown,
  DSLinkButton,
} from "@components/design-system/molecules";
import { DSNoteDisplay } from "@components/design-system/organisms";
import { faFile, faSpinner } from "@fortawesome/free-solid-svg-icons";
import {
  faFileArrowUp,
  faGrid2,
  faLightbulb,
  faRabbitFast,
  faRocket,
  faStamp,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTemplates } from "@hooks";
import { useCompanionV2 } from "@hooks/use-companion-v2";
import { useStartCompanionChat } from "@hooks/use-start-companion-chat";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import WizardStep from "./WizardStep";
import {
  SOAP_TEMPLATE_EXAMPLES,
  SOAP_TEMPLATE_LIBRARY_EXAMPLES,
  FREEFORM_TEMPLATE_LIBRARY_EXAMPLES,
  TEMPLATE_TYPES,
  TEMPLATE_FIELDS,
  TRANSCRIPT_SAMPLE_MAPPINGS,
} from "../constants";
import { useLoadUserTemplate } from "../hooks/useLoadUserTemplate";
import { useTemplateExamples } from "../hooks/useTemplateExamples";
import TemplateFormButton from "../TemplateBuilder/components/common/TemplateFormButton";
import FileUploader from "../TemplateBuilder/components/FileUploader";
import {
  applyBuiltInPreset,
  buildTemplateDropdownOptions,
  resetTemplateFields,
} from "../utils";

const METHOD_TO_SUBVIEW = {
  quill: "quill",
  build: "preset-picker",
  upload: "upload",
};

const QUILL_TIPS = [
  "Build me a SOAP template for a canine wellness exam",
  "Create a template for a puppy litter visit for 1st vaccines",
  "I need a post-op monitoring template for orthopedic surgery",
];

export default function StartingPointStep({
  onNext,
  onFileUpload,
  loadingExtraction,
  control,
  selectedMethod,
  onMethodChange,
}) {
  const { allowCompanionOptIn } = useFlags();
  const { startChat, isStarting } = useStartCompanionChat();
  const {
    sendMessage: sendCompanionV2Message,
    isStreaming: isV2Streaming,
  } = useCompanionV2();
  const { setValue } = useFormContext();
  const templateUuid = useWatch({ control, name: "uuid" });
  const currentType = useWatch({ control, name: "templateType" });
  const templateFields = useWatch({
    control,
    name: "templateFields",
  });
  const { ownedTemplates, sharedTemplates } = useTemplates();
  const { freeformExamples, allSoapExamples, allFreeformExamples } =
    useTemplateExamples();
  const { loadUserTemplate, isLoadingTemplate } =
    useLoadUserTemplate();

  const [subView, setSubView] = useState(
    selectedMethod
      ? METHOD_TO_SUBVIEW[selectedMethod] || "main"
      : "main",
  );
  const [selectedPresetKey, setSelectedPresetKey] = useState(null);

  const handleMethodSelect = (method) => {
    onMethodChange(method);
    mixpanel.track("Wizard Starting Point Selected", {
      templateUuid,
      method,
    });

    switch (method) {
      case "quill":
        startChat();
        setSubView("quill");
        break;
      case "build":
        setSubView("preset-picker");
        break;
      case "upload":
        setSubView("upload");
        break;
    }
  };

  const handlePresetSelect = async (
    presetName,
    templateType,
    option,
  ) => {
    if (option?.isUserTemplate) {
      const result = await loadUserTemplate(presetName);
      if (result) {
        setValue("templateType", result.templateType);
        setValue("templateFields", result.fields, {
          shouldDirty: true,
        });
        setValue("userPreferences", result.userPreferences);
        setValue("templateVariant", presetName);

        mixpanel.track("Wizard Preset Selected", {
          templateUuid,
          presetName: option.label,
          templateType: result.templateType,
          isUserTemplate: true,
        });

        await onNext();
      }
      return;
    }

    const examples =
      templateType === TEMPLATE_TYPES.CUSTOM
        ? allFreeformExamples
        : allSoapExamples;
    const preset = examples[presetName];
    if (!preset) return;

    setValue("templateType", templateType);
    applyBuiltInPreset(setValue, preset, templateType);
    setValue(
      "sampleTranscript",
      preset.sampleTranscript ||
        TRANSCRIPT_SAMPLE_MAPPINGS.DEFAULT.sampleTranscript,
    );
    setValue("templateVariant", presetName);

    mixpanel.track("Wizard Preset Selected", {
      templateUuid,
      presetName,
      templateType,
      isUserTemplate: false,
    });

    await onNext();
  };

  // Main view: three starting point cards
  if (subView === "main") {
    return (
      <WizardStep
        title="Choose Your Starting Point"
        description="How would you like to build your template?"
        hideNext
        hideBack
        dataTestId="wizard-step-starting-point"
      >
        <div
          className={`grid grid-cols-1 gap-md ${allowCompanionOptIn ? "md:grid-cols-3" : "md:grid-cols-2"}`}
        >
          {allowCompanionOptIn && (
            <DSCardWrapper
              interactive={!isStarting}
              onClick={() => {
                if (isStarting) return;
                handleMethodSelect("quill");
              }}
              dataTestId="wizard-option-quill"
            >
              <DSBadge
                variant="green"
                dataTestId="wizard-badge-quill-new"
                size="sm"
              >
                New!
              </DSBadge>
              <div className="flex flex-col items-center gap-sm p-md text-center">
                <Twemoji emoji="🦔" className="text-3xl" />
                <h4 className="text-base font-semibold text-primary">
                  Build With Quill
                </h4>{" "}
                <p className="text-sm text-secondary">
                  Quill the Writing Companion is at your service to
                  help you build your template. Just tell him what you
                  want and he&apos;ll get to work!
                </p>
                <DSBadge
                  icon={faRocket}
                  variant="green"
                  dataTestId="wizard-badge-quill"
                >
                  ~2-5 min
                </DSBadge>
              </div>
            </DSCardWrapper>
          )}

          <DSCardWrapper
            interactive
            onClick={() => handleMethodSelect("build")}
            dataTestId="wizard-option-build"
          >
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <FontAwesomeIcon
                icon={faGrid2}
                className="text-brand text-3xl"
              />
              <h4 className="text-base font-semibold text-primary">
                Build Your Own
              </h4>{" "}
              <p className="text-sm text-secondary">
                Start from a preset or build from scratch. Customize
                every detail of your template step by step.
              </p>
              <DSBadge
                icon={faRabbitFast}
                variant="yellow"
                dataTestId="wizard-badge-build"
              >
                ~5-10 min
              </DSBadge>
            </div>
          </DSCardWrapper>

          <DSCardWrapper
            interactive
            onClick={() => handleMethodSelect("upload")}
            dataTestId="wizard-option-upload"
          >
            <div className="flex flex-col items-center gap-sm p-md text-center">
              <FontAwesomeIcon
                icon={faFileArrowUp}
                className="text-brand text-3xl"
              />
              <h4 className="text-base font-semibold text-primary">
                Upload Your Own
              </h4>
              <p className="text-sm text-secondary">
                Upload a PDF of your own template or a completed note,
                and we&apos;ll identify the sections, formatting, and
                structure to create a template our AI can use.
              </p>
              <DSBadge
                icon={faRabbitFast}
                variant="yellow"
                dataTestId="wizard-badge-upload"
              >
                ~5-10 min
              </DSBadge>
            </div>
          </DSCardWrapper>
        </div>
      </WizardStep>
    );
  }

  // Quill explanation sub-view
  if (subView === "quill") {
    const tipsDisabled = isStarting || isV2Streaming;

    const handleTipClick = async (tip) => {
      try {
        await sendCompanionV2Message(tip);
        mixpanel.track("Wizard Quill Tip Used", { tip });
      } catch (e) {
        console.error("Wizard Quill tip send failed", e);
      }
    };

    return (
      <WizardStep
        title="Build With Quill"
        description="Quill is ready to help you create your template. Chat with him in the sidebar to get started!"
        onBack={() => {
          mixpanel.track("Wizard Quill Cancelled", { templateUuid });
          setSubView("main");
        }}
        hideNext
        dataTestId="wizard-step-quill"
      >
        <DSCardWrapper variant="info" dataTestId="wizard-quill-tips">
          <div className="flex items-start gap-sm">
            <FontAwesomeIcon
              icon={faLightbulb}
              className="text-brand mt-0.5 shrink-0"
            />
            <div className="flex flex-col gap-sm">
              <h4 className="text-sm font-semibold text-primary">
                How to get the best results from Quill
              </h4>
              <p className="text-sm text-secondary">
                Tell Quill about your practice and the kind of
                template you need. The more detail you provide, the
                better the result. Here are some example prompts to
                get you started:
              </p>
              <ul className="flex flex-col gap-xs">
                {QUILL_TIPS.map((tip, index) => (
                  <li
                    key={tip}
                    className="flex items-center gap-sm text-sm text-secondary"
                  >
                    <span className="italic">
                      &ldquo;{tip}&rdquo;
                    </span>
                    <DSLinkButton
                      onClick={() => handleTipClick(tip)}
                      disabled={tipsDisabled}
                      dataTestId={`wizard-quill-tip-${index}`}
                    >
                      Try it
                    </DSLinkButton>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-secondary">
                Quill will update your template’s structure, sections,
                and formatting automatically. You can keep refining it
                until it fits your workflow perfectly.
              </p>
            </div>
          </div>
        </DSCardWrapper>
      </WizardStep>
    );
  }

  // Preset picker sub-view
  if (subView === "preset-picker") {
    const defaultOptions = [
      ...Object.values(SOAP_TEMPLATE_EXAMPLES).map((t) => ({
        label: t.name,
        value: t.name,
      })),
      ...Object.values(freeformExamples).map((t) => ({
        label: t.name,
        value: t.name,
      })),
    ];

    const libraryOptions = [
      ...Object.values(SOAP_TEMPLATE_LIBRARY_EXAMPLES).map((t) => ({
        label: t.name,
        value: t.name,
      })),
      ...Object.values(FREEFORM_TEMPLATE_LIBRARY_EXAMPLES).map(
        (t) => ({
          label: t.name,
          value: t.name,
        }),
      ),
    ];

    const myTemplateOptions = ownedTemplates
      .filter((t) => t.uuid !== templateUuid)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        label: t.name,
        value: t.uuid,
        isUserTemplate: true,
      }));

    const sharedTemplateOptions = sharedTemplates
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((t) => ({
        label: t.name,
        value: t.uuid,
        isUserTemplate: true,
      }));

    const dropdownOptions = buildTemplateDropdownOptions({
      defaultOptions,
      libraryOptions,
      myTemplateOptions,
      sharedTemplateOptions,
    });

    const selectedOption = selectedPresetKey
      ? dropdownOptions.find((o) => o.value === selectedPresetKey)
      : null;
    const isUserTemplate = selectedOption?.isUserTemplate;

    const selectedPreset =
      !isUserTemplate && selectedPresetKey
        ? allSoapExamples[selectedPresetKey] ||
          allFreeformExamples[selectedPresetKey]
        : null;
    const selectedType = selectedPresetKey
      ? isUserTemplate
        ? null
        : allSoapExamples[selectedPresetKey]
          ? TEMPLATE_TYPES.SOAP
          : TEMPLATE_TYPES.CUSTOM
      : null;

    const noteDisplayData = selectedPreset
      ? selectedType === TEMPLATE_TYPES.SOAP
        ? {
            subjective:
              selectedPreset[TEMPLATE_FIELDS.SUBJECTIVE]?.trim() ||
              undefined,
            objective:
              selectedPreset[TEMPLATE_FIELDS.OBJECTIVE]?.trim() ||
              undefined,
            assessment:
              selectedPreset[TEMPLATE_FIELDS.ASSESSMENT]?.trim() ||
              undefined,
            plan:
              selectedPreset[TEMPLATE_FIELDS.PLAN]?.trim() ||
              undefined,
          }
        : {
            content:
              selectedPreset[TEMPLATE_FIELDS.CUSTOM]?.trim() ||
              undefined,
          }
      : null;

    return (
      <WizardStep
        title="Start From a Preset (Optional)"
        description="Choose a preset as your starting point, or skip to build from scratch. You can customize everything in the next step."
        onBack={() => {
          setSelectedPresetKey(null);
          setSubView("main");
        }}
        hideNext
        footerActions={
          <>
            <DSButton
              variant="secondary"
              size="lg"
              onClick={onNext}
              type="button"
              dataTestId="wizard-step-preset-picker-skip-btn"
            >
              Skip
            </DSButton>
            <DSButton
              variant="primary"
              size="lg"
              onClick={() =>
                handlePresetSelect(
                  selectedPresetKey,
                  selectedType,
                  selectedOption,
                )
              }
              disabled={!selectedPresetKey || isLoadingTemplate}
              type="button"
              dataTestId="wizard-step-preset-picker-next-btn"
            >
              {isLoadingTemplate ? "Loading..." : "Next"} →
            </DSButton>
          </>
        }
        dataTestId="wizard-step-preset-picker"
      >
        <div className="flex flex-col gap-lg">
          <DSDropdown
            options={dropdownOptions}
            value={selectedPresetKey}
            onChange={(value) => setSelectedPresetKey(value)}
            placeholder="Choose a preset template..."
            search
            searchPlaceholder="Search templates..."
            width="w-full"
            maxHeight={400}
            dataTestId="wizard-preset-dropdown"
            leadingIcon={faStamp}
            disabled={isLoadingTemplate}
            getOptionTestId={(option) =>
              option.isHeader
                ? `wizard-preset-header-${option.value}`
                : `wizard-preset-${option.value}`
            }
          />

          {selectedPreset && noteDisplayData && (
            <div className="flex flex-col gap-md">
              <div className="flex items-center gap-sm">
                <h5>{selectedPreset.name}</h5>
                <DSBadge
                  variant={
                    selectedType === TEMPLATE_TYPES.SOAP
                      ? "brand"
                      : "blue"
                  }
                  size="sm"
                  dataTestId="wizard-preset-preview-badge"
                >
                  {selectedType === TEMPLATE_TYPES.SOAP
                    ? "SOAP"
                    : "Freeform"}
                </DSBadge>
                <p>{selectedPreset.description}</p>
              </div>

              <DSNoteDisplay
                data={noteDisplayData}
                variant={
                  selectedType === TEMPLATE_TYPES.SOAP
                    ? "soap"
                    : "freeform"
                }
                layout="stacked"
                size="compact"
                showCopyButton={false}
                showAdditionalNotes={false}
                dataTestId="wizard-preset-preview"
              />

              {selectedPreset.preferences && (
                <DSCardWrapper variant="ghost">
                  <span className="text-sm font-semibold text-brand">
                    Preferences
                  </span>
                  <p className="text-xs text-secondary mt-xs line-clamp-2">
                    {selectedPreset.preferences}
                  </p>
                </DSCardWrapper>
              )}
            </div>
          )}

          {isUserTemplate && selectedPresetKey && (
            <div className="flex items-center gap-sm">
              <h5>{selectedOption?.label}</h5>
              <p className="text-sm text-secondary">
                Click Next to load this template as your starting
                point.
              </p>
            </div>
          )}
        </div>
      </WizardStep>
    );
  }

  // Upload sub-view
  if (subView === "upload") {
    const handleUploadTypeChange = (e) => {
      const newType = e.target.value;
      resetTemplateFields(setValue, newType);

      mixpanel.track("Wizard Template Type Selected", {
        templateUuid,
        templateType: newType,
        method: selectedMethod,
      });
    };

    return (
      <WizardStep
        title="Upload Your Template"
        description="Upload a PDF of your existing template or a completed note. Select the format first."
        onBack={() => setSubView("main")}
        onNext={onNext}
        nextDisabled={
          loadingExtraction ||
          !templateFields?.some((f) => f.fieldValue?.trim())
        }
        nextLabel={loadingExtraction ? "Extracting..." : "Next"}
        dataTestId="wizard-step-upload"
      >
        <div className="flex flex-col gap-md">
          <div>
            <label className="block text-sm font-semibold text-primary mb-sm">
              Template Format
            </label>
            <div className="flex gap-sm">
              <TemplateFormButton
                icon={faSoapNote}
                title="SOAP"
                description="This will generate a note in separate Subjective, Objective, Assessment, and Plan sections."
                isSelected={currentType === TEMPLATE_TYPES.SOAP}
                name="templateType"
                value={TEMPLATE_TYPES.SOAP}
                checked={currentType === TEMPLATE_TYPES.SOAP}
                onChange={handleUploadTypeChange}
                dataTestId="wizard-upload-type-soap"
              />
              <TemplateFormButton
                icon={faFile}
                title="Freeform"
                description="This will generate a note in a single text box, formatted however you specify in your template and preferences."
                isSelected={currentType === TEMPLATE_TYPES.CUSTOM}
                name="templateType"
                value={TEMPLATE_TYPES.CUSTOM}
                checked={currentType === TEMPLATE_TYPES.CUSTOM}
                onChange={handleUploadTypeChange}
                dataTestId="wizard-upload-type-freeform"
              />
            </div>
          </div>

          {loadingExtraction ? (
            <div
              className="text-center my-md flex flex-col items-center gap-sm border-2 border-dashed rounded-large p-md border-brand"
              data-testid="wizard-extracting-spinner"
            >
              <span>Extracting the text from your PDF...</span>
              <FontAwesomeIcon
                icon={faSpinner}
                size="2x"
                className="animate-spin text-brand"
              />
            </div>
          ) : (
            <FileUploader
              onFileUpload={async (file) => {
                const success = await onFileUpload(file);
                if (success) await onNext();
              }}
              label="Upload a PDF"
              helperText="Drag and drop or click to select a PDF file (max 10MB)"
              dataTestId="wizard-upload-dropzone"
            />
          )}
        </div>
      </WizardStep>
    );
  }

  return null;
}
